import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * Shared metric card — CampaignOS "Light Enterprise Glassmorphism" style.
 * Frosted glass surface, a tone-coloured icon chip, and a mono value. Self-contained
 * (uses global `cos-*` tokens + `font-mono` + Material Symbols, all available app-wide),
 * so it renders correctly wherever it's used (MIS report, Requests, …) without needing a
 * `.campaign-os` wrapper. Props unchanged: title, value, change, subtitle, color.
 */
const TONE = {
  primary: { chip: 'bg-cos-teal-faint text-cos-teal-deep', icon: 'insights' },
  neutral: { chip: 'bg-cos-surface-hover text-cos-teal-deep', icon: 'analytics' },
  muted: { chip: 'bg-cos-surface-hover text-cos-ink-secondary', icon: 'edit_note' },
  success: { chip: 'bg-cos-teal-faint text-cos-teal-deep', icon: 'task_alt' },
  warning: { chip: 'bg-cos-status-amber-bg text-cos-status-amber', icon: 'pending_actions' },
  destructive: { chip: 'bg-cos-status-coral-bg text-cos-status-coral', icon: 'cancel' },
  danger: { chip: 'bg-cos-status-coral-bg text-cos-status-coral', icon: 'cancel' },
};

// Note: any `icon` passed by callers is intentionally ignored — several callers pass
// lucide names (e.g. 'FileEdit') that aren't Material Symbols. The tone default is used.
const MetricsCard = ({ title, value, change, subtitle, color = 'primary' }) => {
  const tone = TONE[color] || TONE.primary;

  return (
    <div className="relative overflow-hidden rounded-cos-card border border-cos-line bg-white/60 px-4 py-3.5 shadow-cos-glass-sm backdrop-blur-cos-glass transition-all duration-200 hover:-translate-y-0.5 hover:shadow-cos-glass-md">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cos-ink-muted truncate">
          {title}
        </p>
        <span className={cn('grid h-8 w-8 flex-shrink-0 place-items-center rounded-xl', tone.chip)}>
          <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 20" }}>
            {tone.icon}
          </span>
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-mono text-2xl font-bold tabular-nums leading-none tracking-tight text-cos-ink">
          {value}
        </span>
        {change ? <span className="text-[11px] text-cos-ink-muted">{change}</span> : null}
      </div>
      {subtitle ? <p className="mt-1 text-[11px] text-cos-ink-muted truncate">{subtitle}</p> : null}
    </div>
  );
};

export default MetricsCard;
